// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {ExternalLink} from '@app/features/app/components/shared/ExternalLink';
import {HelpCenterArticleSlug} from '@app/features/app/config/HelpCenterConstants';
import {AgeVerificationModal} from '@app/features/auth/components/modals/AgeVerificationModal';
import {NSFWGateReason} from '@app/features/guild/state/GuildNSFWAgree';
import {
	getEffectiveNSFWGeoContext,
	isAgeVerificationAvailableInRegion,
} from '@app/features/moderation/utils/NSFWGeoUtils';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as HelpCenterUtils from '@app/features/ui/utils/HelpCenterUtils';
import {getRegionDisplayName} from '@app/features/user/utils/geo';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback} from 'react';

interface MediaNSFWGateDetailsModalProps {
	reason: NSFWGateReason;
}
export const MediaNSFWGateDetailsModal = observer(({reason}: MediaNSFWGateDetailsModalProps) => {
	const {i18n, t} = useLingui();
	const handleClose = useCallback(() => {
		ModalCommands.pop();
	}, []);
	const handleOpenAgeVerification = useCallback(() => {
		ModalCommands.pop();
		ModalCommands.push(modal(() => <AgeVerificationModal />));
	}, []);
	if (reason === NSFWGateReason.GEO_RESTRICTED) {
		const {countryCode, regionCode} = getEffectiveNSFWGeoContext();
		const regionName = getRegionDisplayName(i18n, countryCode ?? undefined, regionCode ?? undefined);
		const ageVerificationAvailable = isAgeVerificationAvailableInRegion();
		return (
			<Modal.Root size="small" centered>
				<Modal.Header title={t`Age-Restricted Media`} />
				<Modal.Content>
					<Modal.ContentLayout>
						<Modal.Description>
							<Trans>
								Due to age verification laws in {regionName}, this media is not available from your current region.
							</Trans>
						</Modal.Description>
						{ageVerificationAvailable ? (
							<Modal.Description>
								<Trans>Verify your age to unlock age-restricted media.</Trans>
							</Modal.Description>
						) : (
							<Modal.Description>
								<Trans>Age verification is currently available only in the UK.</Trans>
							</Modal.Description>
						)}
					</Modal.ContentLayout>
				</Modal.Content>
				<Modal.Footer>
					{ageVerificationAvailable ? (
						<Button onClick={handleClose} variant="secondary">
							<Trans>Close</Trans>
						</Button>
					) : (
						<Button onClick={handleClose} variant="primary">
							<Trans>Got It</Trans>
						</Button>
					)}
					{ageVerificationAvailable && (
						<Button onClick={handleOpenAgeVerification} variant="primary">
							<Trans>Verify Your Age</Trans>
						</Button>
					)}
				</Modal.Footer>
			</Modal.Root>
		);
	}
	return (
		<Modal.Root size="small" centered>
			<Modal.Header title={t`Age-Restricted Media`} />
			<Modal.Content>
				<Modal.ContentLayout>
					<Modal.Description>
						<Trans>
							You must be 18 or older to view this media.{' '}
							<ExternalLink href={HelpCenterUtils.getURL(HelpCenterArticleSlug.ChangeDateOfBirth)}>
								Learn more
							</ExternalLink>
						</Trans>
					</Modal.Description>
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer>
				<Button onClick={handleClose} variant="primary">
					<Trans>Close</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
